"use client";

import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useRandomFact } from "@ui/hooks/useRandomFact";

interface RandomFactProps {
  /** Delay in ms before the fact animates in. Defaults to 3000. */
  delayMs?: number;
  /** Interval in ms between fact rotations. Defaults to 5000. */
  rotateMs?: number;
}

/**
 * Animated "Did you know?" fact pill.
 * Expands from zero-height after a delay, then rotates facts with a crossfade.
 */
export function RandomFact({ delayMs, rotateMs }: RandomFactProps) {
  const { fact, heading, visible } = useRandomFact(delayMs, rotateMs);

  // Crossfade on fact change
  const [displayedFact, setDisplayedFact] = useState(fact);
  const [fading, setFading] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (fact === displayedFact) return;
    setFading(true);
    timeoutRef.current = setTimeout(() => {
      setDisplayedFact(fact);
      setFading(false);
    }, 300);
    return () => clearTimeout(timeoutRef.current);
  }, [fact, displayedFact]);

  return (
    <div
      className={clsx(
        "tw-grid tw-w-full tw-transition-all tw-duration-700 tw-ease-out",
        visible ? "tw-grid-rows-[1fr] tw-opacity-100" : "tw-grid-rows-[0fr] tw-opacity-0",
      )}
    >
      <div className='tw-overflow-hidden'>
        <div className='tw-pt-3'>
          <div className='tw-mb-3 tw-h-px tw-w-full tw-bg-gray-200' />
          <p className='tw-text-center tw-text-xs tw-leading-relaxed tw-text-gray-500'>
            <span className='tw-font-medium tw-text-gray-600'>{heading}</span>{" "}
            <span className={clsx("tw-transition-opacity tw-duration-300", fading ? "tw-opacity-0" : "tw-opacity-100")}>
              {displayedFact}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
